<template>
  <transition name="modal">
    <div
      v-if="open"
      class="fixed inset-0 z-[100] flex items-center justify-center p-4 font-NotoSerifKhmer"
    >
      <!-- Backdrop -->
      <div
        class="absolute inset-0 bg-black/60 backdrop-blur-md"
        @click="handleClose('close')"
      ></div>

      <!-- Modal Content -->
      <div
        class="relative w-full max-w-2xl max-h-[90vh] overflow-y-auto bg-white/10 backdrop-blur-2xl border border-white/20 rounded-3xl shadow-[0_8px_32px_rgba(0,0,0,0.3)] p-6 md:p-8 animate-scale-up custom-scrollbar"
      >
        <!-- Header -->
        <div class="flex items-center justify-between mb-8">
          <div class="flex items-center gap-4">
            <div
              class="flex items-center justify-center w-12 h-12 rounded-full bg-white/10 border border-white/10 shadow-inner backdrop-blur-md text-amber-400"
            >
              <i class="fa-solid fa-receipt text-xl"></i>
            </div>
            <div class="flex flex-col">
              <h2 class="text-2xl font-bold text-white tracking-tight">
                {{ isEditDoc ? t("Edit Expense") : t("Add New Expense") }}
              </h2>
              <span class="text-sm text-white/50 font-medium"
                >{{ t("Record your wedding costs") }}</span
              >
            </div>
          </div>

          <button
            @click="handleClose('close')"
            class="text-white/40 hover:text-white transition-colors bg-white/5 hover:bg-white/10 p-2.5 rounded-full"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              class="h-5 w-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        <!-- Form -->
        <form @submit.prevent="handleSubmit" class="space-y-6">
          <!-- Layout Grid -->
          <div class="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-6">
            <!-- Expense Name -->
            <div class="md:col-span-2 space-y-2">
              <label for="expenseName" class="text-sm font-medium text-white/80 ml-1">
                {{ t("Expense Title") }} <span class="text-red-400">*</span>
              </label>
              <div class="relative group">
                <input
                  id="expenseName"
                  v-model="expenseName"
                  type="text"
                  :placeholder="t('e.g., Catering, Decoration, Music')"
                  class="glass-input w-full pl-5 pr-4 py-3.5 rounded-2xl outline-none transition-all duration-300"
                  required
                />
              </div>
            </div>

            <!-- Amount -->
            <div class="space-y-2">
              <label for="amount" class="text-sm font-medium text-white/80 ml-1">
                {{ t("Amount") }} <span class="text-red-400">
                  *
                  {{ amount > 0 ? numeralFormat(amount, "0,0[.]00") : "" }}
                </span>
              </label>
              <div class="relative group">
                <input
                  id="amount"
                  v-model="amount"
                  type="number"
                  placeholder="0.00"
                  step="0.01"
                  class="glass-input w-full pl-5 pr-4 py-3.5 rounded-2xl outline-none transition-all duration-300"
                  required
                />
              </div>
            </div>

            <!-- Currency -->
            <div class="relative group space-y-2">
              <label class="text-sm font-medium text-white/80 ml-1">
                {{ t("Currency") }} <span class="text-red-400">*</span>
              </label>
              <div class="relative">
                <select
                  v-model="selectedCurrency"
                  class="glass-input w-full pl-5 pr-10 py-3.5 rounded-2xl outline-none transition-all duration-300 bg-white/5 backdrop-blur-xl border border-white/20 text-white/80 focus:bg-white/10 appearance-none cursor-pointer"
                  required
                >
                  <option class="bg-slate-800 text-white" value="" disabled>{{ t("Select currency") }}</option>
                  <option class="bg-slate-800 text-white" value="USD">USD</option>
                  <option class="bg-slate-800 text-white" value="KHR">KHR</option>
                </select>
                <svg
                  class="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/60 pointer-events-none"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                </svg>
              </div>
            </div>

            <!-- Description -->
            <div class="md:col-span-2 space-y-2">
              <label for="description" class="text-sm font-medium text-white/80 ml-1">
                {{ t("Description") }}
              </label>
              <textarea
                id="description"
                v-model="description"
                rows="3"
                :placeholder="t('Details about this expense (optional)...')"
                class="glass-input w-full px-5 py-4 rounded-2xl outline-none resize-none transition-all duration-300"
              ></textarea>
            </div>
          </div>

          <!-- Actions -->
          <div class="flex items-center justify-end gap-4 pt-6 border-t border-white/10 mt-2">
            <button
              type="button"
              @click="handleClose('close')"
              :disabled="loading"
              class="px-6 py-2.5 rounded-xl text-white/60 font-medium hover:bg-white/10 hover:text-white transition-colors"
            >
              {{ t("Cancel") }}
            </button>

            <button
              type="submit"
              class="relative overflow-hidden px-10 py-2.5 rounded-xl font-bold text-white shadow-lg shadow-amber-500/20 transform active:scale-95 transition-all duration-300 group"
              :class="loading ? 'cursor-not-allowed opacity-80' : ''"
            >
              <div
                class="absolute inset-0 bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-400 hover:to-orange-500 text-white transition-all duration-300 group-hover:scale-105"
              ></div>
              <div class="relative flex items-center gap-2">
                <span
                  v-if="loading"
                  class="animate-spin rounded-full h-4 w-4 border-b-2 border-white"
                ></span>
                <span>{{ isEditDoc ? t("Update Expense") : t("Add Expense") }}</span>
              </div>
            </button>
          </div>
        </form>
      </div>
    </div>
  </transition>
</template>

<script setup>
import { ref, onMounted, watch } from "vue";
import { useI18n } from "vue-i18n";
const { t } = useI18n();
import { serverTimestamp } from "firebase/firestore";
import { projectAuth } from "@/firebase/config";
import useCollection from "@/composables/useDocument";
import { useRoute } from "vue-router";

const props = defineProps({
  visible: Boolean,
  isEditDoc: Boolean,
  doc: Object,
});

const emit = defineEmits(["onClose"]);
const route = useRoute();

const expenseName = ref("");
const amount = ref(null);
const selectedCurrency = ref("USD");
const description = ref("");
const loading = ref(false);
const open = ref(false);

const { addDocs, updateDocs } = useCollection("expenses");

onMounted(() => {
  if (props.visible) open.value = props.visible;
  initForm();
});

watch(() => props.visible, (val) => {
  open.value = val;
  if (val) initForm();
});

const initForm = () => {
  if (props.isEditDoc && props.doc) {
    expenseName.value = props.doc.title || "";
    amount.value = props.doc.amount || null;
    selectedCurrency.value = props.doc.currency || "USD";
    description.value = props.doc.description || "";
  } else {
    handleClear();
  }
};

const handleClear = () => {
  expenseName.value = "";
  amount.value = null;
  selectedCurrency.value = "USD";
  description.value = "";
};

const handleClose = (status) => {
  open.value = false;
  handleClear();
  setTimeout(() => emit("onClose", status), 200);
};

const handleSubmit = async () => {
  try {
    loading.value = true;
    const requestBody = {
      title: expenseName.value,
      userId: projectAuth.currentUser?.uid || "",
      eventId: route.params.id || "",
      amount: parseFloat(amount.value) || 0,
      currency: selectedCurrency.value,
      description: description.value,
    };

    if (!props.isEditDoc) {
      requestBody.createdAt = serverTimestamp();
      requestBody.createdBy = projectAuth.currentUser?.uid || "";
      await addDocs(requestBody);
      handleClose("create");
    } else {
      requestBody.updatedAt = serverTimestamp();
      requestBody.updatedBy = projectAuth.currentUser?.uid || "";
      await updateDocs(props.doc.id, requestBody);
      handleClose("update");
    }
  } catch (err) {
    console.error("Error submitting expense:", err);
  } finally {
    loading.value = false;
  }
};
</script>

<style scoped>
.modal-enter-active,
.modal-leave-active {
  transition: opacity 0.3s ease;
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}

.animate-scale-up {
  animation: scaleUp 0.4s cubic-bezier(0.16, 1, 0.3, 1);
}

@keyframes scaleUp {
  from {
    opacity: 0;
    transform: scale(0.95) translateY(10px);
  }
  to {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
}

.glass-input {
  background: rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  color: white;
}

.glass-input:focus {
  background: rgba(255, 255, 255, 0.1);
  border-color: rgba(255, 255, 255, 0.3);
  box-shadow: 0 0 15px rgba(255, 255, 255, 0.05);
}

.custom-scrollbar::-webkit-scrollbar {
  width: 6px;
}
.custom-scrollbar::-webkit-scrollbar-track {
  background: transparent;
}
.custom-scrollbar::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.1);
  border-radius: 10px;
}
</style>
